import {
  apiSlice,
  createClientV2MutationFn,
  createClientV2QueryFn,
} from 'store/slices/api';
import { parseQueryParams } from 'store/slices/utils';
import {
  ListResponse,
  QueryParams,
  ClientV2POSTRequest,
  ClientV2POSTResponse,
  ClientV2PATCHResponse,
  ClientV2PATCHRequest,
} from 'types/api';
import { CLIENT_V2_URLS } from 'store/slices/constants/clientV2';

import {
  BarcodeLabelPATCHSchema,
  BarcodeLabelPOSTSchema,
  BarcodeLabelSchema,
} from 'types/clientV2/stock';

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getBarcodeLabels: builder.query<
      ListResponse<BarcodeLabelSchema>,
      QueryParams<BarcodeLabelSchema>
    >({
      queryFn: createClientV2QueryFn<
        ListResponse<BarcodeLabelSchema>,
        QueryParams<BarcodeLabelSchema>
      >((params) =>
        CLIENT_V2_URLS.BARCODE_LABELS(
          parseQueryParams<BarcodeLabelSchema>(params)
        )
      ),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }: BarcodeLabelSchema) =>
                  ({ type: 'BarcodeLabels', id } as const)
              ),
              { type: 'BarcodeLabels', id: 'LIST' },
            ]
          : [{ type: 'BarcodeLabels', id: 'LIST' }],
    }),
    createBarcodeLabel: builder.mutation<
      ClientV2POSTResponse<BarcodeLabelSchema>,
      ClientV2POSTRequest<BarcodeLabelPOSTSchema>
    >({
      queryFn: createClientV2MutationFn<
        ClientV2POSTResponse<BarcodeLabelSchema>,
        ClientV2POSTRequest<BarcodeLabelPOSTSchema>
      >((payload) => ({
        url: CLIENT_V2_URLS.BARCODE_LABELS(),
        method: 'POST',
        body: payload,
      })),
      invalidatesTags: [{ type: 'BarcodeLabels', id: 'LIST' }],
    }),
    updateBarcodeLabel: builder.mutation<
      ClientV2PATCHResponse<BarcodeLabelSchema>,
      ClientV2PATCHRequest<BarcodeLabelPATCHSchema>
    >({
      queryFn: createClientV2MutationFn<
        ClientV2PATCHResponse<BarcodeLabelSchema>,
        ClientV2PATCHRequest<BarcodeLabelPATCHSchema>
      >((payload) => ({
        url: CLIENT_V2_URLS.BARCODE_LABELS(),
        method: 'PATCH',
        body: payload,
      })),
      invalidatesTags: (result) => [
        ...(result
          ? result.updatedIds.map(
              (id: BarcodeLabelSchema['id']) =>
                ({ type: 'BarcodeLabels', id } as const)
            )
          : []),
        { type: 'BarcodeLabels', id: 'LIST' },
      ],
    }),
    voidBarcodeLabels: builder.mutation<
      ClientV2PATCHResponse<BarcodeLabelSchema>,
      { ids: BarcodeLabelSchema['id'][]; voided: boolean }
    >({
      queryFn: createClientV2MutationFn<
        ClientV2PATCHResponse<BarcodeLabelSchema>,
        { ids: BarcodeLabelSchema['id'][]; voided: boolean }
      >((data) => ({
        url: CLIENT_V2_URLS.BARCODE_LABELS_VOID(),
        method: 'POST',
        body: data,
      })),
      invalidatesTags: (result) =>
        result
          ? [
              ...result.updatedIds.map((id) => ({
                type: 'BarcodeLabels' as const,
                id,
              })),
              { type: 'BarcodeLabels' as const, id: 'LIST' },
            ]
          : [{ type: 'BarcodeLabels' as const, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetBarcodeLabelsQuery,
  useCreateBarcodeLabelMutation,
  useUpdateBarcodeLabelMutation,
  useVoidBarcodeLabelsMutation,
} = extendedApiSlice;
